import { useId, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { PasswordInput, TextInput } from '../../components/shared/input/Input';
import { PrimaryButton } from '../../components/ui/buttons';
import { toast } from 'react-toastify';
import renderToastError from '../../utils/renderToastError';
import { FormGroup, FormInputDescription, FormInputLabel } from '../../components/form';
import { validateUsername } from '../../utils/validators/validateUsername';
import Page from '../../components/layout/Page';
import { Heading1 } from '../../components/headings';
import { useMutation } from '@tanstack/react-query';
import SignUpFn from '../../api/auth/SignUp';
import { getPasswordStrength } from '../../utils/getPasswordStrength';
import useTurnstile from '../../hooks/useTurnstile';

function percentToColor(percent: number) {
    if (percent < 40) return 'red';
    if (percent < 80) return 'yellow';
    return '#00ff00';
}

export function SignUpPage() {
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');

    const url = new URLSearchParams(useLocation().search);
    const overrideKey = url.get('key');
    const [username, setUsername] = useState(url.get('name') ?? '');
    const turnstileContainerID = useId();
    const { token: turnstileToken, reset: resetCaptcha } = useTurnstile(turnstileContainerID);

    const signupMutation = useMutation({
        mutationFn: ({
            username,
            password,
            overrideKey,
            turnstileToken,
        }: {
            username: string;
            password: string;
            overrideKey?: string;
            turnstileToken: string;
        }) => SignUpFn(username, password, turnstileToken, overrideKey),
    });

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();

        if (password !== passwordConfirm) return toast.error('Passwords must match!');
        if (!turnstileToken) return toast.error('Please complete the captcha challenge.');

        signupMutation.mutate(
            { username, password, overrideKey: overrideKey ?? undefined, turnstileToken },
            {
                onSuccess: (data) => {
                    window.location.href = `/profile/${data.ID}`;
                },
                onError: (err) => {
                    resetCaptcha();
                    toast.error(renderToastError.render({ data: err }));
                },
            },
        );
    }

    const strength = useMemo(() => getPasswordStrength(password), [password]);
    const strengthColor = useMemo(() => percentToColor(strength), [strength]);

    return (
        <Page>
            <div className='w-11/12 md:w-1/2 lg:w-2/6 mx-auto'>
                <Heading1>Sign Up</Heading1>
                {url.get('name') === null && (
                    <div className='my-6'>
                        <p>
                            Already have your name on the site from the sheet days? Contact the staff{' '}
                            <a
                                className='link'
                                href='https://discord.gg/gddl'
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                in our discord
                            </a>{' '}
                            to get a password reset link.
                        </p>
                    </div>
                )}
                <form onSubmit={handleSubmit}>
                    <FormGroup>
                        <FormInputLabel htmlFor='username'>Username</FormInputLabel>
                        <TextInput
                            id='username'
                            value={username}
                            onChange={(e) => setUsername(e.target.value.trimStart())}
                            invalid={!validateUsername(username)}
                            pattern='^[a-zA-Z0-9._]{2,32}$'
                            disabled={url.get('name') !== null}
                            name='username'
                            autoComplete='off'
                            required
                        />
                        <p className='text-gray-400 text-sm'>
                            Name must be between 2 and 32 characters. It can only contain the following characters:{' '}
                            <code>{'a-Z 0-9 . _'}</code>
                        </p>
                    </FormGroup>
                    <FormGroup>
                        <FormInputLabel htmlFor='password'>Password</FormInputLabel>
                        <PasswordInput
                            value={password}
                            onChange={(e) => setPassword(e.target.value.trim())}
                            id='password'
                            name='password'
                            autoComplete='new-password'
                            autoCapitalize='off'
                            minLength={7}
                            invalid={password.length < 7}
                            required
                        />
                        <div
                            className='h-1 round:rounded-b'
                            style={{
                                backgroundImage: `linear-gradient(to left, ${strengthColor}, ${strengthColor})`,
                                backgroundSize: `${strength}%`,
                                backgroundRepeat: 'no-repeat',
                            }}
                        />
                        <FormInputDescription>Passwords must be at least 7 characters long</FormInputDescription>
                    </FormGroup>
                    <FormGroup>
                        <FormInputLabel htmlFor='confirmPassword'>Confirm password</FormInputLabel>
                        <PasswordInput
                            id='confirmPassword'
                            value={passwordConfirm}
                            onChange={(e) => setPasswordConfirm(e.target.value.trimStart())}
                            autoComplete='new-password'
                            autoCapitalize='off'
                            required
                        />
                    </FormGroup>
                    <div id={turnstileContainerID} className='my-4' />
                    <PrimaryButton type='submit' className='w-full'>
                        Sign Up
                    </PrimaryButton>
                </form>
            </div>
        </Page>
    );
}
